/**
 * Per-field escalation to the human caller (BI-1668 / BI-1681 follow-up).
 *
 * The Human Audit in-progress workspace lets the auditor escalate ANY output field —
 * not just the ones the AI auditor flagged as handoff actions. Clicking a field's
 * Escalate icon opens a note box directly under it; Save stages `{field, note}` in the
 * shared store, where it shows up as an entry in the "Escalate to human caller" section
 * and is written to the in-progress human-audit entity when the case is routed to a human
 * call (see HumanAuditTab / DefaultCaseDetailsPage).
 *
 * Wired by CONTEXT rather than props: the field renderers this has to reach
 * (`field-group-accordion` flat scalars and `nested-field-accordion` leaves) sit up to
 * four recursive levels below the board, and both are shared with read-only surfaces
 * (Summary, Human Call, AI Audit). With no provider the control renders NOTHING, so those
 * surfaces are untouched — only the Human Audit board, which provides the context while
 * the board is in EDIT mode, grows the affordance.
 *
 * HIPAA: the note and the field it names may be PHI — display/persist only, never logged.
 */
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import { ArrowUpRight, Trash2 } from 'lucide-react';
import React, { createContext, useContext, useMemo, useState } from 'react';

interface FieldEscalationContextValue {
  /** The note staged for this concrete field path, or undefined when it is not escalated. */
  noteFor?: (fieldPath: string) => string | undefined;
  /** Stage (or replace) the escalation note for this field path. */
  onSave?: (fieldPath: string, note: string) => void;
  /** Drop the staged escalation for this field path. */
  onRemove?: (fieldPath: string) => void;
}

const FieldEscalationContext = createContext<FieldEscalationContextValue>({});

interface FieldEscalationProviderProps extends FieldEscalationContextValue {
  children: React.ReactNode;
}

export const FieldEscalationProvider: React.FC<FieldEscalationProviderProps> = ({
  noteFor,
  onSave,
  onRemove,
  children,
}) => {
  const value = useMemo(() => ({ noteFor, onSave, onRemove }), [noteFor, onSave, onRemove]);
  return (
    <FieldEscalationContext.Provider value={value}>{children}</FieldEscalationContext.Provider>
  );
};

export const useFieldEscalation = (): FieldEscalationContextValue =>
  useContext(FieldEscalationContext);

interface FieldEscalateControlProps {
  /**
   * The field's CONCRETE output path — what gets persisted as the handoff action's
   * `field`. Bracket or dotted indices both resolve (see `resolveHandoffFieldTargets`).
   */
  fieldPath: string;
  /** The field's schema label, used only for accessible names. */
  fieldLabel?: string;
  /** The field's own rendering — laid out beside the Escalate button. */
  children: React.ReactNode;
}

/**
 * WRAPS one field with its Escalate affordance: the field keeps the row and gives up just
 * the button's width on the right, so the icon reads as belonging to that field instead of
 * sitting on a row of its own. Clicking it opens a note box under the field; Save stages
 * the note and CLOSES the box — the staged note is then read and edited in the
 * "Escalate to human caller" section, not repeated under every escalated field (which
 * pushed the board apart). All the field keeps is the icon, lit to mark it escalated;
 * clicking that reopens the note for editing or removal.
 *
 * The button is tooltip-labelled rather than captioned — a per-field text label repeated
 * down a whole board is noise.
 *
 * Renders `children` untouched when no {@link FieldEscalationProvider} is above it.
 */
const FieldEscalateControlComponent: React.FC<FieldEscalateControlProps> = ({
  fieldPath,
  fieldLabel,
  children,
}) => {
  const { noteFor, onSave, onRemove } = useFieldEscalation();
  // `draft` non-null == the note box is open; it holds the in-flight text until Save.
  const [draft, setDraft] = useState<string | null>(null);

  // No provider (Summary / Human Call / AI Audit), or the board is not in edit mode =>
  // the field renders exactly as it did before.
  if (!onSave) return <>{children}</>;

  const staged = noteFor?.(fieldPath);
  const isEscalated = staged !== undefined;
  const isComposing = draft !== null;
  const name = fieldLabel?.trim() || fieldPath;

  const openComposer = () => setDraft(staged ?? '');
  const closeComposer = () => setDraft(null);
  const saveComposer = () => {
    onSave(fieldPath, (draft ?? '').trim());
    setDraft(null);
  };
  const removeEscalation = () => {
    onRemove?.(fieldPath);
    setDraft(null);
  };

  const tooltip = isEscalated
    ? 'Escalated to human caller — edit the note'
    : 'Escalate to human caller';

  return (
    <>
      <div className="flex items-start gap-1">
        {/* min-w-0 so a long value truncates/wraps inside the narrowed field instead of
            pushing the button out of the row. */}
        <div className="min-w-0 flex-1">{children}</div>
        <SimpleTooltipV2 content={tooltip} side="left">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`mt-0.5 h-6 w-6 shrink-0 p-0 ${
              isEscalated
                ? 'bg-primary/10 text-primary hover:text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            aria-pressed={isEscalated}
            aria-label={
              isEscalated
                ? `Edit the escalation note for ${name}`
                : `Escalate ${name} to the human caller`
            }
            onClick={isComposing ? closeComposer : openComposer}
          >
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </SimpleTooltipV2>
      </div>

      {/* The note box, open only while composing. Save closes it — the note lives in the
          "Escalate to human caller" section from then on. */}
      {isComposing && (
        <div className="mt-1.5 rounded-md border border-muted bg-muted/20 px-2 py-2">
          <Textarea
            value={draft ?? ''}
            onChange={e => setDraft(e.target.value)}
            placeholder="Reason for escalation..."
            className="min-h-[56px] text-sm"
            aria-label={`Escalation note for ${name}`}
            autoFocus
          />
          <div className="mt-2 flex items-center justify-end gap-1.5">
            {isEscalated && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mr-auto h-7 px-2.5 text-xs font-medium text-muted-foreground hover:text-destructive"
                aria-label={`Remove the escalation for ${name}`}
                onClick={removeEscalation}
              >
                <Trash2 className="mr-1 h-3 w-3" />
                Remove
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs font-medium"
              onClick={closeComposer}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs font-medium"
              disabled={(draft ?? '').trim() === ''}
              onClick={saveComposer}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export const FieldEscalateControl = React.memo(FieldEscalateControlComponent);
FieldEscalateControl.displayName = 'FieldEscalateControl';
